// inputui.ts ↔ game/InputUI.cpp — InputUIForm (the bottom console bar: black
// fill + side-color separator, MENU button, credits + power displays, the
// keyboard/graffiti scroll arms) and its console controls
// (GraffitiScrollControl, CreditsControl, PowerControl).
//
// The C++ globals are injected: gsim view-pos pinning + gpplrLocal/gapuntc/
// gfMultiplayer → deps.sui (the fingerhandler.ts SimUIForm input slice),
// ggame.GetSimUIForm()->GetPenHandler() → deps.getPenHandler, gtimm →
// deps.gtimm, gpdisp/Host* → optional seams (the web display has no graffiti
// area), gapfnt/gsndm/gevm → the FormMgrHost seams. The InGameMenu modal shell
// is ported (gameoptions.ts DoInGameMenu, wired through deps.inGameMenu);
// TestOptions is DEV_BUILD-only debug (seam kept).

import type { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import { GetColor, GetSideColor, iclr } from '../gfx/palette.ts';
import type { UpdateMap } from '../gfx/updatemap.ts';
import type { Event } from './event.ts';
import { keyDownEvent } from './event.ts';
import { Rect } from './rect.ts';
import type { RectLike } from './rect.ts';
import { FillHelper, vchrBack, kfFrmVisible } from './uikit.ts';
import type { PaintLayer, FormMgr } from '../render/paintengine.ts';
import { CallbackTimer, TimerMgr } from './timer.ts';
import type { SimUIForm as SimUIFormInput } from './fingerhandler.ts';
import { kumStructures } from '../../../sim/src/player/masks.ts';
import type { StructConsts } from '../../../sim/src/gob/unitconsts.ts';

// game/res.h
export const kidfInputUI = 1037; // res.h:34
export const kidcMenuButton = 1002; // res.h:172
export const kidcGraffitiScroll = 1012; // res.h:173
export const kidcAppsSilkButton = 1013; // res.h:174
export const kidcMenuSilkButton = 1014; // res.h:175
export const kidcCalcSilkButton = 1015; // res.h:176
export const kidcFindSilkButton = 1016; // res.h:177
export const kidcCreditsLabel = 1018; // res.h:178
export const kidcPower = 1019; // res.h:179

// game/res.h:75-80
export const kifntHud = 5;

// ht.h:1019 — the placement form's edge-scroll timer (buildui.ts) still runs on
// this 8-tick cadence; the input form's scroll now polls every tick (below).
export const kctMapScrollRate = 8; // TUNE:

// Map-scroll poll rate. Historically the keyboard/edge scroll ran on the 8-tick
// kctMapScrollRate and jumped a whole tile per fire, which read as choppy
// tile-by-tile scrolling. We now poll every tick and integrate a smooth
// velocity instead. The view position is presentation-only (not sim state), so
// finer polling has no determinism / multiplayer / replay impact.
const kctScrollPollRate = 1;

// Smooth keyboard/d-pad pan, in world-coords per tick (kwcTile = 256 WC = one
// tile; ticks are real-time 10 ms). The old code moved a whole tile every 8
// ticks (= 32 WC/tick average); these ramp smoothly to a comparable peak.
const kwcScrollSpeedMax = 48; // peak pan speed  (WC/tick ≈ 6 px/tick ≈ 600 px/s)
const kwcScrollAccel = 8; // ramp-up    (WC/tick added per tick held)
const kwcScrollDecel = 12; // glide-down (WC/tick shed per tick on release)

// Move `v` toward `target` by at most `step` (step >= 0).
function approachValue(v: number, target: number, step: number): number {
  if (v < target) return Math.min(v + step, target);
  if (v > target) return Math.max(v - step, target);
  return v;
}

// ht.h:6748 — ideally a power of 2
export const kcupdSymbolFlashRate = 8;
// ht.h:282
export const kfPerfSymbolFlashing = 0x0200;

// game/sdl/htplatform.h:39
export const vchrMenu = 0x105;

// game/sdl/htplatform.h:58-81
export const keyBitPageUp = 0x0002;
export const keyBitPageDown = 0x0004;
export const keyBitHard1 = 0x0008;
export const keyBitHard2 = 0x0010;
export const keyBitHard3 = 0x0020;
export const keyBitHard4 = 0x0040;
export const keyBitRockerUp = 0x00010000;
export const keyBitRockerDown = 0x00020000;
export const keyBitRockerLeft = 0x00040000;
export const keyBitRockerRight = 0x00080000;
export const keyBitDpadLeft = 0x01000000;
export const keyBitDpadRight = 0x02000000;
export const keyBitDpadButton = 0x04000000; // d-pad center (host.cpp:748 maps RETURN)

// ht.h:8972-8976
export const kircSilkGraffiti = 0;
export const kircSilkApps = 1;
export const kircSilkMenu = 2;
export const kircSilkCalc = 3;
export const kircSilkFind = 4;

/**
 * computeDemandMax — the maximum nPowerDemand over every STRUCTURE unit type, the
 * "yellow zone" threshold PowerControl draws (game/InputUI.cpp:642-655). Extracted
 * (HTML/DOM UI shell plan, Phase 1) from PowerControl::Init so the canonical value
 * has ONE home: the HUD push reads it (instead of pctlPower.m_nDemandMax).
 * It is a per-game CONSTANT (the unit consts never change mid-game), so
 * the InputUIForm caches it once.
 *
 * @param apuntc gsim.ctx.unitConsts — indexed by unit type; the kumStructures bit
 *   mask picks the structure entries (game/InputUI.cpp:644-652).
 */
export function computeDemandMax(apuntc: readonly unknown[]): number {
  let nDemandMax = 0;
  let um = 1;
  for (let i = 0; i < 32; i++, um = (um << 1) >>> 0) {
    if (um & kumStructures) {
      const pstruc = apuntc[i] as StructConsts;
      if (pstruc.nPowerDemand > nDemandMax) {
        nDemandMax = pstruc.nPowerDemand;
      }
    }
  }
  return nDemandMax;
}

/** The injected globals (see the header comment). */
export interface InputUIDeps {
  /** the SimUIForm input slice — gsim.Get/SetViewPos (pinned) + ctx/sim reach. */
  sui: SimUIFormInput;
  /** ggame.GetSimUIForm()->GetPenHandler() (InputUI.cpp:152). */
  getPenHandler: () => { CheckScroll(n?: number): void } | null;
  /** gtimm — the presentation timer list. */
  gtimm: TimerMgr;
  /** gpdisp->GetMode (InputUI.cpp:53) — the web display has no graffiti area. */
  getMode?: () => { cy: number; cyGraffiti: number };
  /** gpdisp->GetSafeAreas (InputUI.cpp:162). */
  getSafeAreas?: () => RectLike;
  /** HostGetSilkRect (InputUI.cpp:109) — dead when cyGraffiti == 0. */
  getSilkRect?: (irc: number) => RectLike;
  /** HostGetCurrentKeyState (InputUI.cpp:132) — keyboard scroll seam, M3-later. */
  hostGetCurrentKeyState?: (keyBits: number) => number;
  /** gpplrLocal->ShowObjectives(ksoObjectives) (InputUI.cpp:262) — the modal
   *  objectives form (ui/objectives.ts); done = the C++ return point. */
  showObjectives?: (done?: () => void) => void;
  /** InGameMenu's ShellForm modal (InputUI.cpp:321-451) — the ported body is
   *  gameoptions.ts DoInGameMenu; GameSession wires it over its ShellEnv. */
  inGameMenu?: () => void;
  /** TestOptions' TestOptionsForm modal (InputUI.cpp:453-464) — DEV_BUILD-only
   *  debug form, not ported (seam kept for harnesses). */
  testOptions?: () => void;
  /** The persistent in-game (Phase 5, Form 5)
   *  HUD BOTTOM BAR renders as a DOM overlay over the live canvas. When set,
   *  InputUIForm hides its OWN display controls (credits / power + the menu button)
   *  so they do NOT double-render under the DOM HUD, and Update() pushes
   *  the live {credits, needCredits, powerDemand, powerSupply, demandMax, flashOn} —
   *  the SAME getters/derivations the canvas reads (demandMax = PowerControl yellow-
   *  zone threshold; flashOn = the symbol-flash phase + perf gate) — to the DOM HUD
   *  each frame (re-render only on change). The
   *  keyboard map-scroll (OnTimer) + the playfield stay on the canvas. Absent
   *  (a headless harness) ⇒ no UI shown. DISPLAY-only — no sim/command/wire change. */
  domHud?: {
    update(state: {
      credits: number;
      needCredits: number;
      powerDemand: number;
      powerSupply: number;
      demandMax: number;
      flashOn: boolean;
      attackMoveArmed?: boolean;
    }): void;
  };
}

//
// InputUIForm implementation
//

// HTML/DOM UI shell plan, Phase 3 (forms.ini elimination): the console (input) bar
// is NO LONGER a forms.ini-loaded Form — it is a renderer-owned PaintLayer. The
// credits/power/menu DISPLAY CONTROLS moved to the DOM HUD bar (Phase 5); all that
// remains on the canvas is OnPaintBackground (the black strip + side-color
// separator the DOM bar overlays) and the keyboard map-scroll (OnTimer). The Form
// base members the engine reads are provided directly — no forms.ini, no controls.
export class InputUIForm implements PaintLayer {
  readonly deps: InputUIDeps;
  // game/InputUI.cpp:14-18 (ctor) — m_pchatter is the chat seam (MP, M3-later)
  m_fTimerAdded = false;
  private m_tmr = new CallbackTimer((t) => this.OnTimer(t));

  // --- PaintLayer identity/geometry (the engine reads these off m_apfrm) ---
  m_pfrmm: FormMgr | null = null;
  m_rc = new Rect();
  m_idf = kidfInputUI; // the cooked-event form id
  m_wf = 0; // kfFrmVisible (set in Init)

  // The canonical power "yellow zone" threshold (Phase 1): max nPowerDemand over
  // the structure unit types (computeDemandMax). A per-game CONSTANT — cached on
  // first read so the HUD push uses it instead of pctlPower.m_nDemandMax.
  private m_nDemandMaxCached = -1;

  // Smooth-scroll state (presentation-only): velocity in WC/tick, a sub-WC
  // accumulator so slow pans don't stall, and the last poll tick for the delta.
  private m_vxScroll = 0;
  private m_vyScroll = 0;
  private m_wxScrollFrac = 0;
  private m_wyScrollFrac = 0;
  private m_tScrollLast = -1;

  constructor(deps: InputUIDeps) {
    this.deps = deps;
  }

  // game/InputUI.cpp:20-24 — ~InputUIForm
  Dispose(): void {
    if (this.m_fTimerAdded) this.deps.gtimm.RemoveTimer(this.m_tmr);
    if (this.m_pfrmm !== null) this.m_pfrmm.RemoveForm(this);
  }

  // game/InputUI.cpp:26-37 — the console-bar Init, re-homed (Phase 3): no forms.ini
  // load, no display controls (credits/power/menu are DOM now). Only the layer rect
  // + the keyboard-scroll timer remain.
  Init(pfrmm: FormMgr): boolean {
    this.m_pfrmm = pfrmm;
    this.m_wf |= kfFrmVisible;
    this.SizeAndPositionEverything();

    this.deps.gtimm.AddTimer(this.m_tmr, kctScrollPollRate);
    this.m_fTimerAdded = true;

    return true;
  }

  // game/InputUI.cpp:39-42
  OnDisplayResize(): void {
    this.SizeAndPositionEverything();
  }

  // game/InputUI.cpp:44-114 — size the layer to cover the console strip. The control
  // positioning (menu/credits/power + the silk buttons) is GONE — the DOM HUD bar
  // owns its own layout. Only the layer rect (which the engine's paint/hit-test walk
  // reads) remains. The graffiti area is always absent on the web build (cyGraffiti 0).
  SizeAndPositionEverything(): void {
    const pbm = this.m_pfrmm!.GetDib();
    const sizDib = pbm.GetSize();
    const mode = this.deps.getMode?.() ?? { cy: sizDib.cy, cyGraffiti: 0 };
    const cyInputForm = sizDib.cy + mode.cyGraffiti;
    const rcT = new Rect();
    rcT.Set(0, 0, sizDib.cx, cyInputForm);
    this.SetRect(rcT);
  }

  // game/InputUI.cpp:116-127
  Update(): void {
    // (Phase 3 — forms.ini elimination): the credits/power readouts are DOM-only now
    // (the canvas controls are deleted). Push the live values to the DOM HUD bar each
    // frame; a render-only host (node oracle / replay viewer) leaves domHud absent and
    // there is simply no HUD readout (it is chrome, not playfield). DISPLAY-only.
    if (this.deps.domHud === undefined) return;
    const plrLocal = this.deps.sui.ctx.plrLocal!;

    // The power "yellow zone" threshold — the max per-structure nPowerDemand
    // (game/InputUI.cpp:642-655) — the CANONICAL computeDemandMax (cached per game).
    if (this.m_nDemandMaxCached < 0) {
      this.m_nDemandMaxCached = computeDemandMax(this.deps.sui.ctx.unitConsts);
    }
    // The credit-/power-symbol FLASH (the canvas blanked the symbol glyph every
    // kcupdSymbolFlashRate (8) sim updates, GATED on kfPerfSymbolFlashing). Replicate
    // the EXACT phase from sim.GetUpdateCount(): symbol drawn (flashOn) on even
    // half-periods; forced true when the perf option is off. The DOM view toggles the
    // "$"/power symbol in lockstep with this single phase.
    const cUpdates = this.deps.sui.sim.GetUpdateCount();
    const flashing = (this.deps.sui.ctx.wfPerfOptions & kfPerfSymbolFlashing) !== 0;
    const flashOn = !flashing || (Math.trunc(cUpdates / kcupdSymbolFlashRate) & 1) === 0;
    this.deps.domHud.update({
      credits: plrLocal.GetCredits(),
      needCredits: plrLocal.GetNeedCreditsCount(),
      powerDemand: plrLocal.GetPowerDemand(),
      powerSupply: plrLocal.GetPowerSupply(),
      demandMax: this.m_nDemandMaxCached,
      flashOn,
      attackMoveArmed: this.deps.sui.IsAttackMoveArmed(),
    });
  }

  // game/InputUI.cpp:129-154 — polled every tick (kctScrollPollRate). The C++
  // moved the view a whole tile per fire; we integrate a smooth velocity so the
  // map pans at the pixel level. View position is presentation-only.
  OnTimer(tCurrent: number): void {
    // Ticks since the last poll. Normally 1, but stay robust to catch-up when
    // several ticks pump in one frame (and to the very first poll).
    let n = this.m_tScrollLast < 0 ? 1 : tCurrent - this.m_tScrollLast;
    this.m_tScrollLast = tCurrent;
    if (n < 1) n = 1;
    else if (n > 8) n = 8; // after a stall, don't let the camera lurch

    this.UpdateKeyboardScroll(n);
    this.deps.getPenHandler()?.CheckScroll(n); // edge auto-scroll, scaled by n
  }

  // Smooth keyboard/d-pad pan: ramp a velocity toward the held direction and
  // integrate it into the view position (InputUI.cpp:129-154 stepped a whole
  // tile per fire instead).
  private UpdateKeyboardScroll(n: number): void {
    // (Phase 3) the console bar is now a PaintLayer, not a Form — but it still sits
    // in the input FormMgr's m_apfrm, so the FormMgr.GetFocus walk (topmost focusable
    // across every region) returns THIS layer exactly when it would have returned the
    // input Form before: only when no other focusable form (a menu/dialog/build panel
    // /unit menu) is up. The keyboard pans the map only then — the identical gate.
    const root = this.m_pfrmm!.GetRoot(); // gpmfrmm
    const inGameActive = root !== null && root.GetFocus() === this;
    const dwKeys = inGameActive
      ? (this.deps.hostGetCurrentKeyState ?? (() => 0))(
          keyBitDpadLeft |
            keyBitDpadRight |
            keyBitPageUp |
            keyBitPageDown |
            keyBitHard1 |
            keyBitHard2 |
            keyBitHard3 |
            keyBitHard4 |
            keyBitRockerUp |
            keyBitRockerDown |
            keyBitRockerLeft |
            keyBitRockerRight,
        )
      : 0;

    let dirX = 0;
    let dirY = 0;
    if (dwKeys & (keyBitPageUp | keyBitRockerUp)) dirY -= 1;
    if (dwKeys & (keyBitPageDown | keyBitRockerDown)) dirY += 1;
    if (dwKeys & (keyBitDpadLeft | keyBitRockerLeft | keyBitHard1 | keyBitHard2)) dirX -= 1;
    if (dwKeys & (keyBitDpadRight | keyBitRockerRight | keyBitHard3 | keyBitHard4)) dirX += 1;

    // Accelerate toward the target while a key is held; glide to a stop on release.
    this.m_vxScroll = approachValue(
      this.m_vxScroll,
      dirX * kwcScrollSpeedMax,
      (dirX !== 0 ? kwcScrollAccel : kwcScrollDecel) * n,
    );
    this.m_vyScroll = approachValue(
      this.m_vyScroll,
      dirY * kwcScrollSpeedMax,
      (dirY !== 0 ? kwcScrollAccel : kwcScrollDecel) * n,
    );

    if (this.m_vxScroll === 0 && this.m_vyScroll === 0) {
      this.m_wxScrollFrac = 0;
      this.m_wyScrollFrac = 0;
      return;
    }

    // Integrate velocity into whole-WC steps, carrying the sub-WC remainder so
    // slow speeds still move.
    this.m_wxScrollFrac += this.m_vxScroll * n;
    this.m_wyScrollFrac += this.m_vyScroll * n;
    const dWx = Math.trunc(this.m_wxScrollFrac);
    const dWy = Math.trunc(this.m_wyScrollFrac);
    this.m_wxScrollFrac -= dWx;
    this.m_wyScrollFrac -= dWy;
    if (dWx === 0 && dWy === 0) return;

    const wpt = { wx: 0, wy: 0 };
    this.deps.sui.GetViewPos(wpt); // gsim.GetViewPos
    this.deps.sui.SetViewPos(wpt.wx + dWx, wpt.wy + dWy); // gsim.SetViewPos

    // If a map edge clamped an axis, drop its velocity so it doesn't wind up and
    // overshoot when the player reverses direction.
    const wptAfter = { wx: 0, wy: 0 };
    this.deps.sui.GetViewPos(wptAfter);
    if (dWx !== 0 && wptAfter.wx === wpt.wx) {
      this.m_vxScroll = 0;
      this.m_wxScrollFrac = 0;
    }
    if (dWy !== 0 && wptAfter.wy === wpt.wy) {
      this.m_vyScroll = 0;
      this.m_wyScrollFrac = 0;
    }
  }

  // game/InputUI.cpp:156-172 — the console-bar fill: black strip + 1px side-color
  // (MP) / cyan (SP) separator. The DOM HUD bar overlays this (pointer-events:none),
  // but it stays so the canvas under the bar is the right color (Phase 3 step 3).
  OnPaintBackground(pbm: CpuDibBitmap, pupd: UpdateMap | null): void {
    // Fill background

    const rcT = new Rect();
    const prcSafeAreas = this.deps.getSafeAreas?.() ?? { left: 0, top: 0, right: 0, bottom: 0 };
    rcT.Set(0, 0, this.m_rc.right, this.m_rc.bottom + prcSafeAreas.bottom);
    FillHelper(pbm, pupd, rcT, GetColor(iclr.kiclrBlack));

    // Draw separating line

    rcT.bottom = rcT.top + 1;

    const clr = this.deps.sui.ctx.fMultiplayer
      ? GetSideColor(this.deps.sui.ctx.plrLocal!.GetSide()) // gaiclrSide[side]
      : GetColor(iclr.kiclr0CyanSide);
    FillHelper(pbm, pupd, rcT, clr);
  }

  // (Phase 3) the layer paints only the background fill; there is no canvas image
  // or control tree, so OnPaint / OnPaintControls are no-ops.
  OnPaint(_pbm: CpuDibBitmap): void {}
  OnPaintControls(_pbm: CpuDibBitmap, _pupd: UpdateMap | null): void {}

  // game/InputUI.cpp:174-217 — the menu keys (MENU / BACK / 'm') open the in-game
  // menu. (Phase 3) there is no control tree, so the base Form's control-walk
  // fallthrough is gone; only the keydown arm remains (the credits/menu buttons are
  // DOM-HUD controls now, routed through main.ts, not this layer's EventProc).
  EventProc(pevt: Event): boolean {
    if (pevt.eType === keyDownEvent) {
      switch (pevt.chr) {
        case vchrMenu:
        case vchrBack:
        case 0x6d: // 'm'
          this.InGameMenu();
          return true;
      }
    }
    return false;
  }

  // game/InputUI.cpp:321-451 — the modal in-game menu (ShellForm over
  // gpiniForms kidfInGameMenu + the game-over/save/load/options dispatch).
  // The body lives in gameoptions.ts DoInGameMenu (the M4b port), reached
  // through the seam GameSession wires.
  InGameMenu(): void {
    this.deps.inGameMenu?.();
  }

  // game/InputUI.cpp:453-464 — the modal test-options form (DEV_BUILD-only
  // debug; not ported). Seam kept for harnesses.
  TestOptions(): void {
    this.deps.testOptions?.();
  }

  // --- PaintLayer scaffolding the engine reads (the Form base provided these) ---
  GetId(): number {
    return this.m_idf;
  }
  GetFlags(): number {
    return this.m_wf;
  }
  GetFormMgr(): FormMgr | null {
    return this.m_pfrmm;
  }
  GetRect(prc: Rect): void {
    prc.SetRc(this.m_rc);
  }
  SetRect(prc: Rect): void {
    this.InvalidateRect(null);
    this.m_rc.SetRc(prc);
    this.InvalidateRect(null);
  }
  InvalidateRect(prc: Rect | null): void {
    if (this.m_pfrmm === null) return;
    this.m_pfrmm.InvalidateRect(prc ?? this.m_rc);
  }
  // the console strip has no pen handling of its own (the DOM HUD owns its taps);
  // a pointer on the bar that isn't a DOM control falls through to nothing.
  OnHitTest(pevt: Event): boolean {
    if (!(this.m_wf & kfFrmVisible)) return false;
    return this.m_rc.PtIn(pevt.x, pevt.y);
  }
  OnKeyTest(_pevt: Event | null): boolean {
    return true;
  }
  OnPenEvent(_pevt: Event): boolean {
    return false;
  }
  OnUpdateMapInvalidate(_pupd: UpdateMap, _prcOpaque: Rect): void {}
  ScrollInvalidate(_pupd: UpdateMap | null): void {}
  OnScroll(_dx: number, _dy: number): void {}
  FrameStart(): void {}
  FrameComplete(): void {}
  BreakCapture(): void {}
}
